import { useState } from 'react';
import { Plus, X } from 'lucide-react';

interface ColumnManagerProps {
  columns: string[];
  onChange: (cols: string[]) => void;
  disabled?: boolean;
}

export function ColumnManager({ columns, onChange, disabled = false }: ColumnManagerProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const add = () => {
    const name = draft.trim();
    if (!name || columns.includes(name)) { setDraft(''); setAdding(false); return; }
    onChange([...columns, name]);
    setDraft('');
    setAdding(false);
  };

  const remove = (col: string) => onChange(columns.filter(c => c !== col));

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-100">Buckets</h2>
        <span className="text-xs text-nightshade-400 dark:text-nightshade-500">— SFDC statuses map to these buckets</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {columns.map(col => (
          <span
            key={col}
            className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full bg-blueberry-50 dark:bg-blueberry-950/40 text-blueberry-700 dark:text-blueberry-300 border border-blueberry-200 dark:border-blueberry-800 text-xs font-medium"
          >
            {col}
            {columns.length > 1 && !disabled && (
              <button
                onClick={() => remove(col)}
                className="text-blueberry-400 hover:text-blueberry-700 dark:hover:text-blueberry-200 transition-colors"
                aria-label={`Remove ${col}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}

        {!disabled && (adding ? (
          <div className="flex items-center gap-1.5">
            <input
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') add(); if (e.key === 'Escape') { setAdding(false); setDraft(''); } }}
              placeholder="Column name…"
              className="px-2.5 py-1 text-xs bg-white dark:bg-nightshade-800 border border-nightshade-300 dark:border-nightshade-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blueberry-400 w-36 text-nightshade-900 dark:text-nightshade-50"
            />
            <button onClick={add} className="text-xs text-blueberry-500 hover:text-blueberry-700 transition-colors font-medium">Add</button>
            <button onClick={() => { setAdding(false); setDraft(''); }} className="text-xs text-nightshade-400 hover:text-nightshade-600 transition-colors">Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-nightshade-300 dark:border-nightshade-600 text-xs text-nightshade-500 dark:text-nightshade-400 hover:border-blueberry-400 hover:text-blueberry-500 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add column
          </button>
        ))}
      </div>
    </div>
  );
}
